import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Grid, Typography, Button, Chip, CircularProgress,
  Alert, Card, CardContent, TextField, Stepper, Step, StepLabel,
  Divider, useTheme, useMediaQuery, Fade
} from '@mui/material';
import {
  ArrowBack, ArrowForward, CheckCircle, CalendarMonth,
  Person, Email, Phone, Message,
  Bed, Bathtub, Tv, Balcony, LocalParking,
  Restaurant, Warning, WaterDrop, AirportShuttle,
  Kitchen, Weekend, LocalOffer, ContentCopy,
  ConfirmationNumber
} from '@mui/icons-material';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import api from '../services/api';

const Booking = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [activeStep, setActiveStep] = useState(0);
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [bookingResult, setBookingResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const [formData, setFormData] = useState({
    checkIn: new Date(Date.now() + 86400000),
    checkOut: new Date(Date.now() + 86400000 * 3),
    guestName: '',
    guestEmail: '',
    guestPhone: '',
    specialRequests: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchRoom = async () => {
      try {
        const res = await api.get(`/guesthouse/rooms/${roomId}`);
        setRoom(res.data.data);
      } catch (err) {
        setError('Failed to load room details');
      } finally {
        setLoading(false);
      }
    };
    fetchRoom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  // PRICE CALCULATION — $60 base, $50 for 5+ nights
  const nights = Math.max(1, Math.ceil(
    (formData.checkOut - formData.checkIn) / (1000 * 60 * 60 * 24)
  ));

  const basePrice = room?.pricePerNight || 60;
  const pricePerNight = nights >= 5 ? 50 : basePrice;
  const discountApplied = nights >= 5;
  const subtotal = nights * pricePerNight;
  const originalSubtotal = nights * basePrice;
  const savings = originalSubtotal - subtotal;
  const total = subtotal;

  const validateStep1 = () => {
    const errs = {};
    if (!formData.checkIn) errs.checkIn = 'Select check-in date';
    if (!formData.checkOut) errs.checkOut = 'Select check-out date';
    if (formData.checkOut <= formData.checkIn) errs.checkOut = 'Must be after check-in';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateStep2 = () => {
    const errs = {};
    if (!formData.guestName.trim()) errs.guestName = 'Name is required';
    if (!formData.guestEmail.trim()) errs.guestEmail = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.guestEmail)) {
      errs.guestEmail = 'Enter a valid email';
    }
    if (!formData.guestPhone.trim()) errs.guestPhone = 'Phone is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    if (activeStep === 0 && !validateStep1()) return;
    if (activeStep === 1 && !validateStep2()) return;
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setErrors({});
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: '' });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const res = await api.post('/bookings/guest', {
        roomId,
        checkIn: formData.checkIn.toISOString(),
        checkOut: formData.checkOut.toISOString(),
        guests: 1,
        guestName: formData.guestName,
        guestEmail: formData.guestEmail,
        guestPhone: formData.guestPhone,
        specialRequests: formData.specialRequests
      });

      setBookingResult(res.data.data);
      setActiveStep(3);
    } catch (err) {
      setError(err.response?.data?.message || 'Booking failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const copyCode = () => {
    if (bookingResult?.code) {
      navigator.clipboard.writeText(bookingResult.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" sx={{ bgcolor: '#f5f0e8' }}>
        <CircularProgress size={70} thickness={3} sx={{ color: '#1a237e' }} />
      </Box>
    );
  }

  if (error && !room) {
    return (
      <Box sx={{ p: 4, maxWidth: 600, mx: 'auto', mt: 8 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          onClick={() => navigate('/')}
          sx={{ mt: 3 }}
          variant="contained"
          startIcon={<ArrowBack />}
        >
          Back to Home
        </Button>
      </Box>
    );
  }

  const steps = ['Dates', 'Your Details', 'Review & Confirm'];

  return (
    <Box sx={{
      background: 'linear-gradient(180deg, #f5f0e8 0%, #faf8f3 50%, #ede5d8 100%)',
      minHeight: '100vh',
      width: '100%',
      py: { xs: 3, md: 6 },
      px: { xs: 2, md: 4 }
    }}>
      <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>

        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{
            mb: 3,
            color: '#1a237e',
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': { bgcolor: 'rgba(26,35,126,0.08)' }
          }}
        >
          Back to Home
        </Button>

        <Box mb={4} textAlign="center">
          <Typography
            variant="overline"
            sx={{ color: '#1a237e', fontWeight: 700, letterSpacing: 3, fontSize: '0.9rem' }}
          >
            BOOK YOUR STAY
          </Typography>
          <Typography variant={isMobile ? 'h5' : 'h3'} fontWeight={800} sx={{ mt: 1, color: '#1a237e' }}>
            Reserve the Entire House
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            3 quick steps — no account needed
          </Typography>
        </Box>

        <Box sx={{
          mb: 4,
          bgcolor: '#fdfbf6',
          p: { xs: 2, md: 3 },
          borderRadius: '20px',
          boxShadow: '0 8px 30px rgba(26,35,126,0.08)',
          border: '1px solid rgba(26,35,126,0.08)',
          overflow: 'hidden'
        }}>
          <Stepper activeStep={activeStep} alternativeLabel={isMobile}>
            {steps.map((label, i) => (
              <Step key={label} completed={activeStep > i}>
                <StepLabel
                  StepIconProps={{
                    sx: {
                      '&.Mui-completed': { color: '#4caf50' },
                      '&.Mui-active': { color: '#1a237e' }
                    }
                  }}
                >
                  <Typography
                    fontWeight={600}
                    color={activeStep >= i ? '#1a237e' : '#999'}
                    sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' } }}
                  >
                    {label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Grid container spacing={{ xs: 2, md: 4 }}>

          <Grid item xs={12} md={8}>
            <Fade in key={activeStep} timeout={500}>
              <Box>

                {/* STEP 1 — DATES ONLY */}
                {activeStep === 0 && (
                  <Card sx={{
                    borderRadius: '24px',
                    boxShadow: '0 15px 50px rgba(26,35,126,0.1)',
                    bgcolor: '#fdfbf6',
                    border: '1px solid rgba(26,35,126,0.08)'
                  }}>
                    <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
                      <Typography
                        variant={isMobile ? 'h6' : 'h5'}
                        fontWeight={700}
                        color="#1a237e"
                        gutterBottom
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 2, md: 4 } }}
                      >
                        <CalendarMonth sx={{ color: '#1a237e' }} />
                        When would you like to stay?
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" fontWeight={600} color="#1a237e" mb={1}>
                            Check-in Date
                          </Typography>
                          <DatePicker
                            selected={formData.checkIn}
                            onChange={(date) => setFormData({ ...formData, checkIn: date })}
                            selectsStart
                            startDate={formData.checkIn}
                            endDate={formData.checkOut}
                            minDate={new Date()}
                            customInput={
                              <TextField
                                fullWidth
                                error={!!errors.checkIn}
                                helperText={errors.checkIn}
                                InputProps={{
                                  sx: {
                                    borderRadius: '12px',
                                    bgcolor: 'white',
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(26,35,126,0.15)' },
                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1a237e' },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1a237e' }
                                  }
                                }}
                              />
                            }
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" fontWeight={600} color="#1a237e" mb={1}>
                            Check-out Date
                          </Typography>
                          <DatePicker
                            selected={formData.checkOut}
                            onChange={(date) => setFormData({ ...formData, checkOut: date })}
                            selectsEnd
                            startDate={formData.checkIn}
                            endDate={formData.checkOut}
                            minDate={new Date(formData.checkIn.getTime() + 86400000)}
                            customInput={
                              <TextField
                                fullWidth
                                error={!!errors.checkOut}
                                helperText={errors.checkOut}
                                InputProps={{
                                  sx: {
                                    borderRadius: '12px',
                                    bgcolor: 'white',
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(26,35,126,0.15)' },
                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1a237e' },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1a237e' }
                                  }
                                }}
                              />
                            }
                          />
                        </Grid>
                      </Grid>

                      {/* DURATION SUMMARY */}
                      <Box sx={{
                        mt: 3,
                        p: 2.5,
                        bgcolor: 'rgba(26,35,126,0.05)',
                        borderRadius: '16px',
                        border: '1px solid rgba(26,35,126,0.1)'
                      }}>
                        <Typography variant="body1" color="#1a237e" fontWeight={600}>
                          🗓️ {nights} night{nights > 1 ? 's' : ''}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {formData.checkIn.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} → {formData.checkOut.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </Typography>
                        {discountApplied && (
                          <Chip
                            icon={<LocalOffer sx={{ color: 'white !important' }} />}
                            label="🎉 Discount Applied! 5+ nights → $50/night"
                            sx={{
                              mt: 2,
                              bgcolor: '#4caf50',
                              color: 'white',
                              fontWeight: 700,
                              fontSize: { xs: '0.7rem', md: '0.85rem' }
                            }}
                          />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* STEP 2 — GUEST DETAILS */}
                {activeStep === 1 && (
                  <Card sx={{
                    borderRadius: '24px',
                    boxShadow: '0 15px 50px rgba(26,35,126,0.1)',
                    bgcolor: '#fdfbf6',
                    border: '1px solid rgba(26,35,126,0.08)'
                  }}>
                    <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
                      <Typography
                        variant={isMobile ? 'h6' : 'h5'}
                        fontWeight={700}
                        color="#1a237e"
                        gutterBottom
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 2, md: 4 } }}
                      >
                        <Person sx={{ color: '#1a237e' }} />
                        Your Details
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Full Name"
                            name="guestName"
                            value={formData.guestName}
                            onChange={handleChange}
                            error={!!errors.guestName}
                            helperText={errors.guestName}
                            InputProps={{
                              startAdornment: <Person sx={{ mr: 1, color: '#999' }} />,
                              sx: { borderRadius: '12px', bgcolor: 'white' }
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Email Address"
                            name="guestEmail"
                            type="email"
                            value={formData.guestEmail}
                            onChange={handleChange}
                            error={!!errors.guestEmail}
                            helperText={errors.guestEmail || 'Confirmation will be sent here'}
                            InputProps={{
                              startAdornment: <Email sx={{ mr: 1, color: '#999' }} />,
                              sx: { borderRadius: '12px', bgcolor: 'white' }
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Phone Number"
                            name="guestPhone"
                            value={formData.guestPhone}
                            onChange={handleChange}
                            error={!!errors.guestPhone}
                            helperText={errors.guestPhone}
                            InputProps={{
                              startAdornment: <Phone sx={{ mr: 1, color: '#999' }} />,
                              sx: { borderRadius: '12px', bgcolor: 'white' }
                            }}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Special Requests (Optional)"
                            name="specialRequests"
                            value={formData.specialRequests}
                            onChange={handleChange}
                            multiline
                            rows={3}
                            placeholder="Early check-in, dietary needs..."
                            InputProps={{
                              startAdornment: <Message sx={{ mr: 1, color: '#999', mt: 1 }} />,
                              sx: { borderRadius: '12px', bgcolor: 'white' }
                            }}
                          />
                        </Grid>
                      </Grid>

                      <Box sx={{
                        mt: 3,
                        p: 2,
                        bgcolor: 'rgba(76,175,80,0.08)',
                        borderRadius: '16px',
                        border: '1px solid rgba(76,175,80,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                      }}>
                        <CheckCircle sx={{ color: '#4caf50', flexShrink: 0 }} />
                        <Typography variant="body2" color="#1a237e" sx={{ fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
                          <strong>No account needed!</strong> You'll receive a <strong>booking code</strong> to manage your reservation.
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* STEP 3 — REVIEW */}
                {activeStep === 2 && (
                  <Card sx={{
                    borderRadius: '24px',
                    boxShadow: '0 15px 50px rgba(26,35,126,0.1)',
                    bgcolor: '#fdfbf6',
                    border: '1px solid rgba(26,35,126,0.08)'
                  }}>
                    <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
                      <Typography
                        variant={isMobile ? 'h6' : 'h5'}
                        fontWeight={700}
                        color="#1a237e"
                        gutterBottom
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 2, md: 4 } }}
                      >
                        <CheckCircle sx={{ color: '#1a237e' }} />
                        Review Your Booking
                      </Typography>

                      <Box sx={{ mb: 3 }}>
                        <Typography variant="overline" color="text.secondary" fontWeight={700}>
                          Stay Details
                        </Typography>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Check-in</Typography>
                            <Typography variant="body2" fontWeight={600} color="#1a237e">
                              {formData.checkIn.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Check-out</Typography>
                            <Typography variant="body2" fontWeight={600} color="#1a237e">
                              {formData.checkOut.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Nights</Typography>
                            <Typography variant="body2" fontWeight={600} color="#1a237e">
                              {nights} night{nights > 1 ? 's' : ''}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>

                      <Divider sx={{ my: 3 }} />

                      <Box sx={{ mb: 3 }}>
                        <Typography variant="overline" color="text.secondary" fontWeight={700}>
                          Guest Information
                        </Typography>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">Name</Typography>
                            <Typography variant="body2" fontWeight={600} color="#1a237e">
                              {formData.guestName}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">Email</Typography>
                            <Typography variant="body2" fontWeight={600} color="#1a237e" sx={{ wordBreak: 'break-all' }}>
                              {formData.guestEmail}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">Phone</Typography>
                            <Typography variant="body2" fontWeight={600} color="#1a237e">
                              {formData.guestPhone}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>

                      {formData.specialRequests && (
                        <>
                          <Divider sx={{ my: 3 }} />
                          <Box>
                            <Typography variant="overline" color="text.secondary" fontWeight={700}>
                              Special Requests
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                              "{formData.specialRequests}"
                            </Typography>
                          </Box>
                        </>
                      )}

                      {discountApplied && (
                        <Alert
                          severity="success"
                          icon={<LocalOffer />}
                          sx={{ mt: 3, borderRadius: '12px' }}
                        >
                          <strong>Discount Applied!</strong> You saved ${savings} (5+ nights at $50/night)
                        </Alert>
                      )}

                      {error && (
                        <Alert severity="error" sx={{ mt: 3, borderRadius: '12px' }}>
                          {error}
                        </Alert>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* STEP 4 — SUCCESS */}
                {activeStep === 3 && bookingResult && (
                  <Card sx={{
                    borderRadius: '24px',
                    boxShadow: '0 30px 80px rgba(76,175,80,0.25)',
                    bgcolor: '#fdfbf6',
                    textAlign: 'center',
                    overflow: 'hidden'
                  }}>
                    <Box sx={{
                      background: 'linear-gradient(135deg, #4caf50, #388e3c)',
                      p: { xs: 3, md: 4 },
                      color: 'white'
                    }}>
                      <Box sx={{
                        width: { xs: 70, md: 100 },
                        height: { xs: 70, md: 100 },
                        borderRadius: '50%',
                        bgcolor: 'rgba(255,255,255,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                        animation: 'pop 0.6s ease-out'
                      }}>
                        <CheckCircle sx={{ fontSize: { xs: 50, md: 70 } }} />
                      </Box>
                      <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={800} gutterBottom>
                        🎉 Booking Confirmed!
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.95 }}>
                        Thank you, {bookingResult.booking?.guestName}!
                      </Typography>
                    </Box>

                    <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
                      <Box sx={{
                        p: { xs: 3, md: 4 },
                        background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
                        borderRadius: '20px',
                        mb: { xs: 3, md: 4 },
                        color: 'white'
                      }}>
                        <Chip
                          icon={<ConfirmationNumber sx={{ color: 'white !important' }} />}
                          label="YOUR BOOKING CODE"
                          sx={{
                            bgcolor: 'rgba(255,255,255,0.2)',
                            color: 'white',
                            fontWeight: 700,
                            letterSpacing: 2,
                            mb: 2,
                            fontSize: { xs: '0.7rem', md: '0.85rem' }
                          }}
                        />
                        <Typography
                          variant={isMobile ? 'h4' : 'h2'}
                          fontWeight={900}
                          sx={{
                            fontFamily: 'monospace',
                            letterSpacing: { xs: 2, md: 4 },
                            mb: 2
                          }}
                        >
                          {bookingResult.code}
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<ContentCopy />}
                          onClick={copyCode}
                          sx={{
                            bgcolor: copied ? '#4caf50' : 'rgba(255,255,255,0.2)',
                            color: 'white',
                            borderRadius: '50px',
                            px: 3, py: 1,
                            textTransform: 'none',
                            fontWeight: 600,
                            border: '1px solid rgba(255,255,255,0.3)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                          }}
                        >
                          {copied ? '✓ Copied!' : 'Copy Code'}
                        </Button>
                      </Box>

                      <Alert
                        severity="info"
                        sx={{
                          borderRadius: '16px',
                          mb: 3,
                          textAlign: 'left',
                          bgcolor: 'rgba(26,35,126,0.05)',
                          border: '1px solid rgba(26,35,126,0.15)',
                          '& .MuiAlert-icon': { color: '#1a237e' }
                        }}
                      >
                        <Typography variant="body2" fontWeight={600} color="#1a237e" gutterBottom sx={{ fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
                          📌 Save this code!
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                          Use this code to <strong>view, manage, or cancel</strong> your booking anytime.
                          We've also sent it to <strong>{bookingResult.booking?.guestEmail}</strong>
                        </Typography>
                      </Alert>

                      <Typography variant="body2" color="text.secondary" paragraph>
                        💰 <strong>Total: ${bookingResult.booking?.totalPrice}</strong> — Pay at check-in
                      </Typography>

                      <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap" mt={3}>
                        <Button
                          variant="contained"
                          onClick={() => navigate('/my-bookings')}
                          startIcon={<ConfirmationNumber />}
                          sx={{
                            background: 'linear-gradient(135deg, #1a237e, #0d47a1)',
                            borderRadius: '50px',
                            px: { xs: 3, md: 4 },
                            py: { xs: 1.2, md: 1.5 },
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: { xs: '0.85rem', md: '1rem' }
                          }}
                        >
                          Manage Booking
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => navigate('/')}
                          sx={{
                            borderColor: '#1a237e',
                            color: '#1a237e',
                            borderRadius: '50px',
                            px: { xs: 3, md: 4 },
                            py: { xs: 1.2, md: 1.5 },
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: { xs: '0.85rem', md: '1rem' },
                            borderWidth: 2,
                            '&:hover': { borderWidth: 2, bgcolor: 'rgba(26,35,126,0.05)' }
                          }}
                        >
                          Back to Home
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </Box>
            </Fade>

            {activeStep < 3 && (
              <Box display="flex" justifyContent="space-between" mt={3}>
                <Button
                  onClick={handleBack}
                  disabled={activeStep === 0}
                  startIcon={<ArrowBack />}
                  sx={{
                    borderRadius: '50px',
                    px: { xs: 2, md: 4 },
                    py: { xs: 1.2, md: 1.5 },
                    textTransform: 'none',
                    fontWeight: 600,
                    color: '#1a237e',
                    fontSize: { xs: '0.85rem', md: '1rem' },
                    '&:hover': { bgcolor: 'rgba(26,35,126,0.05)' }
                  }}
                >
                  Back
                </Button>

                {activeStep < 2 ? (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    endIcon={<ArrowForward />}
                    sx={{
                      background: 'linear-gradient(135deg, #1a237e, #0d47a1)',
                      borderRadius: '50px',
                      px: { xs: 3, md: 5 },
                      py: { xs: 1.2, md: 1.5 },
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: { xs: '0.85rem', md: '1rem' },
                      boxShadow: '0 8px 25px rgba(26,35,126,0.35)',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 12px 35px rgba(26,35,126,0.5)'
                      }
                    }}
                  >
                    Continue
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={submitting}
                    endIcon={!submitting && <CheckCircle />}
                    sx={{
                      background: 'linear-gradient(135deg, #4caf50, #388e3c)',
                      borderRadius: '50px',
                      px: { xs: 3, md: 5 },
                      py: { xs: 1.2, md: 1.5 },
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: { xs: '0.85rem', md: '1rem' },
                      boxShadow: '0 8px 25px rgba(76,175,80,0.35)',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 12px 35px rgba(76,175,80,0.5)'
                      }
                    }}
                  >
                    {submitting ? 'Booking...' : 'Confirm'}
                  </Button>
                )}
              </Box>
            )}
          </Grid>

          {/* SIDEBAR */}
          <Grid item xs={12} md={4}>
            <Box sx={{ position: { md: 'sticky' }, top: { md: 24 } }}>

              <Card sx={{
                borderRadius: '24px',
                boxShadow: '0 15px 50px rgba(26,35,126,0.1)',
                overflow: 'hidden',
                mb: 3,
                bgcolor: '#fdfbf6'
              }}>
                <Box
                  component="img"
                  src={room?.images?.[0] || '/images/1.jpg'}
                  alt={room?.name}
                  sx={{ width: '100%', height: { xs: 160, md: 200 }, objectFit: 'cover' }}
                />
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  <Typography variant="overline" color="#1a237e" fontWeight={700}>
                    YOUR ROOM
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color="#1a237e" gutterBottom>
                    {room?.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 2, fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
                    {room?.description?.substring(0, 100)}...
                  </Typography>

                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {room?.amenities?.slice(0, 3).map((a, i) => (
                      <Chip
                        key={i}
                        label={a}
                        size="small"
                        sx={{
                          bgcolor: '#f5f0e8',
                          color: '#1a237e',
                          fontWeight: 600,
                          fontSize: '0.65rem'
                        }}
                      />
                    ))}
                    {(room?.amenities?.length || 0) > 3 && (
                      <Chip
                        label={`+${room.amenities.length - 3}`}
                        size="small"
                        sx={{ bgcolor: '#e8eaf6', color: '#1a237e', fontWeight: 700, fontSize: '0.65rem' }}
                      />
                    )}
                  </Box>
                </CardContent>
              </Card>

              <Card sx={{
                borderRadius: '24px',
                boxShadow: '0 15px 50px rgba(26,35,126,0.15)',
                bgcolor: '#fdfbf6',
                border: discountApplied ? '2px solid #4caf50' : '2px solid rgba(26,35,126,0.15)'
              }}>
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  <Typography variant="h6" fontWeight={700} color="#1a237e" gutterBottom>
                    💰 Price Summary
                  </Typography>

                  {discountApplied && (
                    <Chip
                      icon={<LocalOffer sx={{ color: 'white !important' }} />}
                      label="DISCOUNT APPLIED"
                      size="small"
                      sx={{
                        mb: 2,
                        bgcolor: '#4caf50',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.7rem'
                      }}
                    />
                  )}

                  <Box sx={{ mt: 2 }}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2" color="text.secondary">
                        ${pricePerNight} × {nights} night{nights > 1 ? 's' : ''}
                      </Typography>
                      <Typography variant="body2" fontWeight={600} color="#1a237e">
                        ${subtotal}
                      </Typography>
                    </Box>

                    {discountApplied && (
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2" color="#4caf50" fontWeight={600}>
                          🎉 You save
                        </Typography>
                        <Typography variant="body2" fontWeight={700} color="#4caf50">
                          -${savings}
                        </Typography>
                      </Box>
                    )}

                    <Divider sx={{ my: 2 }} />

                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6" fontWeight={700} color="#1a237e">
                        Total
                      </Typography>
                      <Typography variant="h5" fontWeight={800} color="#1a237e">
                        ${total}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{
                    mt: 2,
                    p: 1.5,
                    bgcolor: 'rgba(76,175,80,0.08)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <CheckCircle sx={{ color: '#4caf50', fontSize: 18 }} />
                    <Typography variant="caption" color="#2e7d32" fontWeight={600} sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}>
                      Pay at check-in — no prepayment
                    </Typography>
                  </Box>

                  <Box sx={{
                    mt: 2,
                    p: 1.5,
                    bgcolor: 'rgba(26,35,126,0.05)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <Warning sx={{ color: '#1a237e', fontSize: 18 }} />
                    <Typography variant="caption" color="#1a237e" fontWeight={600} sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}>
                      Free cancellation up to 24h before
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

            </Box>
          </Grid>
        </Grid>
      </Box>

      <style>
        {`
          @keyframes pop {
            0% { transform: scale(0); }
            50% { transform: scale(1.15); }
            100% { transform: scale(1); }
          }
        `}
      </style>
    </Box>
  );
};

export default Booking;